import React from 'react';

import {
  ActionIcon,
  Box,
  Button,
  Container,
  Group,
  SegmentedControl,
  Text,
  createStyles,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';

import { ProgramCalendarView } from '../types';

interface WeekNavigationHeaderProps {
  view: ProgramCalendarView;
  setView: React.Dispatch<React.SetStateAction<ProgramCalendarView>>;
  weekStart: string;
  weekEnd: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}

const useStyles = createStyles(theme => ({
  wrapper: {
    width: '100%',
    fontFamily: `${theme.fontFamily} !important`,
    backgroundColor: theme.colors.gray[0],
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
}));

const WeekNavigationHeader = ({
  view,
  setView,
  weekStart,
  weekEnd,
  onPrev,
  onNext,
  onToday,
}: WeekNavigationHeaderProps): JSX.Element => {
  const { classes } = useStyles();
  const start = dayjs(weekStart);
  const end = dayjs(weekEnd);
  const rangeLabel = `${start.format('MMMM D')} – ${end.format('MMMM D')}`;

  return (
    <Container fluid className={classes.wrapper}>
      <Group grow noWrap spacing={0}>
        <Box />
        <Group position='center'>
          <SegmentedControl
            data-cy='programCalendarViewSwitcher'
            size='sm'
            value={view}
            onChange={(value: ProgramCalendarView) => setView(value)}
            data={[
              { label: 'Month', value: 'month' },
              { label: 'Week', value: 'week' },
              { label: 'Day', value: 'agenda' },
            ]}
          />
        </Group>
        <Group position='right' noWrap spacing='xs'>
          <Button
            variant='subtle'
            color='kiloBlue'
            size='sm'
            compact
            onClick={onToday}
            data-cy='weekNavToday'
          >
            Today
          </Button>
          <ActionIcon
            onClick={onPrev}
            variant='subtle'
            color='kiloBlue'
            data-cy='weekNavPrev'
            aria-label='Previous week'
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
          <Text size='sm' fw={700} color='kiloBlue.6' data-cy='weekRangeLabel'>
            {rangeLabel}
          </Text>
          <ActionIcon
            onClick={onNext}
            variant='subtle'
            color='kiloBlue'
            data-cy='weekNavNext'
            aria-label='Next week'
          >
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
      </Group>
    </Container>
  );
};

export default WeekNavigationHeader;
